import { Injectable, OnDestroy } from '@angular/core';
import {
  BehaviorSubject,
  catchError,
  finalize,
  forkJoin,
  Observable,
  of,
  Subject,
  takeUntil,
  tap
} from 'rxjs';
import {
  GoalChronologyDTO,
  MatchEventDetailsDTO,
  MatchInfoDTO,
  MatchPlayerStatsDetailsDTO,
  StatisticsService
} from '@solverml/api';

/**
 * "Cerebro" (State Service) para la página de detalle del partido.
 * Gestiona la carga de toda la información relacionada con un
 * único partido (info, stats de jugadores, eventos y cronología de goles).
 */
@Injectable({
  providedIn: 'root'
})
export class MatchDetailStateService implements OnDestroy {

  // --- ESTADO DE CARGA ---
  private isLoadingSubject = new BehaviorSubject<boolean>(false);
  public isLoading$ = this.isLoadingSubject.asObservable();

  // --- ESTADO DE DATOS ---
  private matchInfoSubject = new BehaviorSubject<MatchInfoDTO | null>(null);
  public matchInfo$ = this.matchInfoSubject.asObservable();

  private playerStatsSubject = new BehaviorSubject<MatchPlayerStatsDetailsDTO[] | null>(null);
  public playerStats$ = this.playerStatsSubject.asObservable();

  private matchEventsSubject = new BehaviorSubject<MatchEventDetailsDTO[] | null>(null);
  public matchEvents$ = this.matchEventsSubject.asObservable();

  private goalChronologySubject = new BehaviorSubject<GoalChronologyDTO | null>(null);
  public goalChronology$ = this.goalChronologySubject.asObservable();

  // --- INTERNO ---
  private destroy$ = new Subject<void>();
  private currentMatchId: number | null = null;

  constructor(private statsApi: StatisticsService) {}

  /**
   * Limpia todos los observables y resetea el estado.
   * Debe ser llamado en el ngOnDestroy del PageComponent.
   */
  public resetState(): void {
    this.isLoadingSubject.next(false);
    this.matchInfoSubject.next(null);
    this.playerStatsSubject.next(null);
    this.matchEventsSubject.next(null);
    this.goalChronologySubject.next(null);
    this.currentMatchId = null;
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  /**
   * Carga TODOS los datos de un partido en paralelo.
   * @param matchId El ID del partido (obtenido de la ruta).
   */
  public loadData(matchId: number): void {

    // Evitar recargas innecesarias si ya se está cargando o es el mismo partido
    if (this.isLoadingSubject.value && this.currentMatchId === matchId) {
      return;
    }

    this.isLoadingSubject.next(true);
    this.currentMatchId = matchId;

    // Limpiamos datos antiguos
    this.matchInfoSubject.next(null);
    this.playerStatsSubject.next(null);
    this.matchEventsSubject.next(null);
    this.goalChronologySubject.next(null);

    // Usamos forkJoin para cargar todos los endpoints en paralelo
    forkJoin({
      info: this.statsApi.getMatchInfoEndpoint(matchId),
      playerStats: this.statsApi.getMatchPlayerStatsEndpoint(matchId),
      events: this.statsApi.getMatchEventsEndpoint(matchId),
      chronology: this.statsApi.getMatchGoalChronologyEndpoint(matchId)
    }).pipe(
      tap(({ info, playerStats, events, chronology }) => {
        // Si todo tiene éxito, actualizamos todos los sujetos
        this.matchInfoSubject.next(info);
        this.playerStatsSubject.next(playerStats);
        this.matchEventsSubject.next(events);
        this.goalChronologySubject.next(chronology);
      }),
      catchError(err => {
        console.error(`Error loading match details for match ${matchId}`, err);
        // Si CUALQUIERA de las llamadas falla, reseteamos todo
        this.resetState();
        return of(null);
      }),
      finalize(() => {
        this.isLoadingSubject.next(false);
      }),
      takeUntil(this.destroy$)
    ).subscribe();
  }
}
